import React, { FunctionComponent, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  View,
  Text,
  StyleSheet,
  Linking,
  TouchableOpacity,
  useWindowDimensions,
} from "react-native";
import uuid from "react-uuid";
import SignatureCanvas from "react-signature-canvas";
import { PRIVACY_URL, TERMS_URL } from "../../constants";
import { Store, Visit } from "../../types";
import { buildMedicalHistoryPayload } from "../../utils/medicalHistory";
import { ConsentFlowState, reset } from "../../state/consentFlow";
import { pick } from "lodash";
import dataURLtoBlob from "blueimp-canvas-to-blob";

import Button from "../../components/Button";
import { colors } from "../../theme";
import { useDispatch, useSelector } from "react-redux";
import StickyFooter from "../../components/StickyFooter";
import CheckBox from "../../components/CheckBox";
import Heading from "../../components/Heading";
import { CREATE_VISIT, UPDATE_VISIT } from "./gql";
import { uploadBlob } from "../../utils/presignedUpload";
import { RootState } from "../../state";

type Params = {
  store: Store;
  visit?: Visit;
};

function buildPayloadFromConsentState(state: ConsentFlowState) {
  const consentAnswers = Object.keys(state.answeredConsentQuestions).map(
    (questionId) => state.answeredConsentQuestions[questionId],
  );
  return {
    customerId: state.subject.id,
    medicalHistory: buildMedicalHistoryPayload(
      state.medicalHistory,
      state.medicalConcerns,
    ),
    consentAnswers,
    services: Object.keys(state.services).map((key) => {
      const service = state.services[key];

      // For existing services we always want to also send over the id
      // but we can't use the id field to verify because the id is present
      // on all services (it is generated on the client for new services)
      const fields = service.isExistingService
        ? ["id", "serviceId", "submittedInformation"]
        : ["serviceId", "submittedInformation"];

      return pick(service, fields);
    }),
  };
}

interface SignatureCaptureProps {
  route: any;
  navigation: any;
  isUnscheduled: boolean;
}

const SignatureCaptureWeb: FunctionComponent<SignatureCaptureProps> = ({
  route,
  navigation,
  isUnscheduled,
}) => {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const { width } = useWindowDimensions();
  const signatureWidth = Math.max(280, Math.min(width - 40, 560));
  const signatureHeight = width < 420 ? 180 : 240;

  const { store, visit }: Params = route.params;
  const dispatch = useDispatch();
  const [validSignature, setValidSignature] = useState(false);
  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const client = useApolloClient();
  const consentState = useSelector((state: RootState) => state.consentFlow);

  const [createOrUpdateVisit] = useMutation(
    visit ? UPDATE_VISIT : CREATE_VISIT,
    {
      onCompleted: ({ result }) => {
        setIsUploading(false);
        if (result.visit) {
          dispatch(reset());
          navigation.replace("VisitConfirmed", {
            visitId: result.visit.id,
          });
        } else {
          const details = (result.errors || [])
            .map((error: { field?: string; message: string }) =>
              error.field ? error.field + ": " + error.message : error.message,
            )
            .join(", ");
          setSubmitError(details || "The API did not create the visit.");
        }
      },
      onError: (error) => {
        setIsUploading(false);
        const details = [
          error.message,
          ...(error.graphQLErrors || []).map((item) => item.message),
          error.networkError?.message,
        ]
          .filter(Boolean)
          .join("; ");
        setSubmitError(details || "The visit request failed.");
      },
    },
  );

  const uploadFile = async () => {
    try {
      setSubmitError(null);
      setIsUploading(true);
      const fileName = uuid();
      const canvas = sigCanvasRef.current?.getTrimmedCanvas();

      if (!canvas) {
        throw new Error("Signature canvas is empty");
      }

      const dataUrl = canvas.toDataURL("image/png");
      const blob = dataURLtoBlob(dataUrl);
      const file = new File([blob], fileName as string, { type: "image/png" });
      const signatureUrl = await uploadBlob(
        client,
        file,
        "customer-signatures",
      );
      await createOrUpdateVisit({
        variables: {
          input: {
            ...buildPayloadFromConsentState(consentState),
            ...(visit ? { visitId: visit.id } : { storeId: store.id }),
            customerSignatureImageUrl: signatureUrl,
            unscheduled: isUnscheduled,
            consentAnsweredAt: new Date(),
          },
        },
      });
    } catch (error) {
      setIsUploading(false);
      const message = error instanceof Error ? error.message : String(error);
      setSubmitError("Signature submission failed: " + message);
    }
  };

  const handleClear = () => {
    sigCanvasRef.current?.clear();
    setValidSignature(false);
  };

  const handleOnEnd = () => {
    const strokes = ((sigCanvasRef.current?.toData() ||
      []) as unknown) as Array<unknown[] | { points?: unknown[] }>;
    const pointCount = strokes.reduce(
      (total, stroke) =>
        total +
        (Array.isArray(stroke) ? stroke.length : stroke.points?.length || 0),
      0,
    );
    setValidSignature(pointCount >= 6);
  };

  const handleConfirm = () => {
    if (validSignature && hasAcceptedAgreement) {
      uploadFile();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.headingRow}>
          <View>
            <Heading size="l" style={styles.heading}>
              Add your signature
            </Heading>
            <Text style={styles.supportingText}>
              Use your finger, mouse, or trackpad.
            </Text>
          </View>
          <Text style={styles.finalStep}>Final step</Text>
        </View>

        <Text style={styles.contextText}>
          Signing for {store.name} - Visit consent
        </Text>

        <View
          style={[
            styles.signatureCard,
            { width: signatureWidth, height: signatureHeight + 74 },
          ]}
        >
          <View style={styles.signatureCardHeader}>
            <Text
              style={[
                styles.signatureStatus,
                validSignature && styles.signatureStatus__valid,
              ]}
            >
              {validSignature ? "Signature added" : "Signature required"}
            </Text>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={handleClear}
              style={styles.clearButton}
            >
              <Text style={styles.clearButtonText}>Clear</Text>
            </TouchableOpacity>
          </View>
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor={colors.black}
            backgroundColor={colors.white}
            onEnd={handleOnEnd}
            canvasProps={{
              height: signatureHeight,
              width: signatureWidth - 32,
              style: { width: signatureWidth - 32, height: signatureHeight },
            }}
          />
          <View style={styles.signatureLineContainer}>
            <Text style={styles.xCharacter}>X</Text>
            <View style={styles.signatureLine} />
          </View>
        </View>
      </View>

      <StickyFooter>
        <View style={styles.footerContent}>
          <CheckBox
            isChecked={hasAcceptedAgreement}
            onPress={setHasAcceptedAgreement}
          >
            <Text style={styles.agreementLabel}>
              I agree this is my electronic signature.
            </Text>
          </CheckBox>

          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setShowAgreement(!showAgreement)}
            style={styles.disclosureButton}
          >
            <Text style={styles.disclosureButtonText}>
              Electronic signature agreement {showAgreement ? "-" : "+"}
            </Text>
          </TouchableOpacity>

          {showAgreement && (
            <View style={styles.disclosureContent}>
              <Text style={styles.conditionText}>
                My submitted signature is legally binding like a handwritten
                signature, and I consent to receive information electronically.
              </Text>
              <View style={styles.linkRow}>
                <Text
                  accessibilityRole="link"
                  onPress={() => Linking.openURL(TERMS_URL)}
                  style={styles.conditionText__Link}
                >
                  Terms of service
                </Text>
                <Text
                  accessibilityRole="link"
                  onPress={() => Linking.openURL(PRIVACY_URL)}
                  style={styles.conditionText__Link}
                >
                  Privacy policy
                </Text>
              </View>
            </View>
          )}

          {submitError && <Text style={styles.errorText}>{submitError}</Text>}

          <Button
            title="Sign and submit visit"
            onPress={handleConfirm}
            disabled={!validSignature || !hasAcceptedAgreement || isUploading}
            isLoading={isUploading}
          />
          <Text style={styles.submitHint}>
            {validSignature && hasAcceptedAgreement
              ? "Ready to submit securely."
              : "Add a signature and accept the agreement to continue."}
          </Text>
        </View>
      </StickyFooter>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.black,
    justifyContent: "space-between",
  },
  content: {
    width: "100%",
    maxWidth: 600,
    alignSelf: "center",
    paddingHorizontal: 20,
    paddingTop: 24,
  },
  headingRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  heading: {
    color: colors.white,
  },
  supportingText: {
    color: colors.midGrey,
    marginTop: 4,
  },
  finalStep: {
    color: colors.black,
    backgroundColor: colors.yellow,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    fontSize: 12,
  },
  contextText: {
    color: colors.midGrey,
    fontSize: 12,
    marginTop: 16,
    marginBottom: 12,
  },
  signatureCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    alignSelf: "center",
    overflow: "hidden",
  },
  signatureCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 30,
  },
  signatureStatus: {
    color: colors.darkGrey,
    fontSize: 12,
  },
  signatureStatus__valid: {
    color: colors.statusGreen,
  },
  clearButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  clearButtonText: {
    color: colors.black,
    fontSize: 12,
    textDecorationLine: "underline",
  },
  signatureLineContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: -22,
    pointerEvents: "none",
  },
  xCharacter: {
    color: colors.black,
    fontSize: 18,
    marginRight: 8,
  },
  signatureLine: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
  },
  footerContent: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
  },
  agreementLabel: {
    color: colors.white,
    marginLeft: 12,
    paddingRight: 32,
  },
  disclosureButton: {
    marginTop: 14,
    marginBottom: 8,
  },
  disclosureButtonText: {
    color: colors.yellow,
    fontSize: 13,
  },
  disclosureContent: {
    marginBottom: 12,
  },
  conditionText: {
    color: colors.midGrey,
    fontSize: 12,
  },
  linkRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  conditionText__Link: {
    color: colors.yellow,
    textDecorationLine: "underline",
    marginRight: 20,
    fontSize: 12,
  },
  errorText: {
    color: colors.red,
    textAlign: "center",
    marginBottom: 10,
    fontSize: 13,
  },
  submitHint: {
    color: colors.midGrey,
    textAlign: "center",
    marginTop: 8,
    fontSize: 12,
  },
});

export default SignatureCaptureWeb;
